import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { MessageSquareIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appPageCopy from '@/i18n/ns/app-page';
import appSharingCopy from '@/i18n/ns/app-sharing';
import appsNavCopy from '@/i18n/ns/apps-nav';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import type { ArtifactCanvasTarget, DraftCanvasTarget } from '../artifacts/routes';
import { companyRequest } from '@/company/api';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { useCompany } from '@/company/context';
import { useOptionalWorkspace } from '../context';
import { ErrorState, LoadingState } from '../primitives';
import { isAppPageMode } from './app-page-mode';
import { useSessionPicture } from './session-picture';
import './app-opening.css';

// Opening a saved App, on its own page or beside a chat: from the click to the live App it is one look. The App's
// own picture (its Apps card's), dimmed, or its name tile on a soft tint, with its name and a quiet line that it is
// opening; the live App crossfades over it when its frame reports ready (RuntimeFrame `opening`). When it cannot
// open, the same place says so calmly, with what to do next: try again, or Change with Chat.

type OpeningHint = { name: string; picture: string | null };
// What an Apps card knew is private to the signed-in person in that company: the cache is keyed by that principal and
// artifact id, and it is emptied the moment another principal (or none, after a logout) reads or writes it.
const hints = new Map<string, OpeningHint>();
let hintsOwner: string | null = null;

/** The authenticated principal the hints belong to (user and company), or null while nobody is signed in. */
export function useAppOpeningPrincipal(): string | null {
  const { data } = useCompany();
  const userId = data?.membership?.userId;
  const companyId = data?.company?.id;
  const principal = userId && companyId ? JSON.stringify([userId, companyId]) : null;
  // A logout or another person signing in empties the cache at once, not only at the next read.
  useEffect(() => {
    scoped(principal);
  }, [principal]);
  return principal;
}

function scoped(principal: string | null) {
  if (hintsOwner !== principal) {
    hints.clear();
    hintsOwner = principal;
  }
  return principal !== null;
}

const readHint = (principal: string | null, artifactId: string) =>
  scoped(principal) ? hints.get(artifactId) : undefined;

/** What the Apps card knew, kept for the moment its App opens (by Artifact id, for this principal only). */
export function rememberAppOpening(
  principal: string | null,
  artifactId: string,
  hint: OpeningHint,
) {
  if (scoped(principal)) hints.set(artifactId, hint);
}

/** A logout or session change: nothing the previous person's cards knew stays behind. */
export function forgetAppOpening() {
  hints.clear();
  hintsOwner = null;
}

/** Change with Chat, when the App's own page offers it (app-saved-display.tsx). */
export const ChangeWithChatContext = createContext<{ open: () => void; busy: boolean } | null>(
  null,
);

function AppStage({
  artifactId,
  name,
  children,
}: {
  artifactId: string;
  name?: string;
  children: (title: string) => ReactNode;
}) {
  const principal = useAppOpeningPrincipal();
  const hint = readHint(principal, artifactId);
  const title = name || hint?.name || '';
  // The card's own picture (read with the session where it must be); the name tile when there is none or it fails.
  const { src: shown } = useSessionPicture(hint?.picture ?? null);
  const [failed, setFailed] = useState<string | null>(null);
  const picture = shown && failed !== shown ? shown : null;
  return (
    <div className="ap-opening" data-picture={picture ? 'true' : undefined}>
      {picture ? (
        <img
          className="ap-opening-picture"
          src={picture}
          alt=""
          onError={() => setFailed(picture)}
        />
      ) : (
        title && (
          <span className="ap-opening-tile" aria-hidden="true">
            {Array.from(title.trim())[0]?.toUpperCase()}
          </span>
        )
      )}
      <div className="ap-opening-note">{children(title)}</div>
    </div>
  );
}

/** `preparing`: the App's version is still being checked before it opens for this person. */
export function AppOpening({
  artifactId,
  name,
  preparing = false,
}: {
  artifactId: string;
  name?: string;
  preparing?: boolean;
}) {
  const copy = useNamespaceCopy(appsNavCopy);
  const sharing = useNamespaceCopy(appSharingCopy);
  const [named, unnamed] = preparing
    ? [sharing.preparing, sharing.preparingUnnamed]
    : [copy.appsOpening, copy.appsOpeningUnnamed];
  return (
    <AppStage artifactId={artifactId} name={name}>
      {(title) => (
        <div
          className="ap-opening-status"
          role="status"
          data-testid={preparing ? 'app-preparing' : undefined}
        >
          <p>{title ? formatCopy(named, { app: title }) : unnamed}</p>
          <span className="ap-opening-progress" aria-hidden="true" />
        </div>
      )}
    </AppStage>
  );
}

/** The App could not open: what is wrong, and what to do (Try again when it can help, Change with Chat). */
export function AppCannotOpen({
  artifactId,
  name,
  onRetry,
  reason,
}: {
  artifactId: string;
  name?: string;
  onRetry?: () => void;
  reason?: string;
}) {
  const copy = useNamespaceCopy(appsNavCopy);
  const retry = useNamespaceCopy(artifactPanelsCopy).retry;
  const pageCopy = useNamespaceCopy(appPageCopy);
  const session = useSessionBoundFetch();
  const [withdrawn, setWithdrawn] = useState<{ ownerName: string | null } | null>(null);
  useEffect(() => {
    let alive = true;
    setWithdrawn(null);
    void companyRequest<{ state: string; ownerName?: string | null }>(
      `/api/v1/company/apps/${encodeURIComponent(artifactId)}/access-state`,
      'GET',
      undefined,
      session,
    )
      .then((state) => {
        if (alive && state.state === 'unshared')
          setWithdrawn({ ownerName: state.ownerName ?? null });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [artifactId, session]);
  const sharingCopy = useNamespaceCopy(appSharingCopy);
  const changeLabel = pageCopy.changeInChat;
  const change = useContext(ChangeWithChatContext);
  return (
    <AppStage artifactId={artifactId} name={name}>
      {(title) => (
        <div className="ap-opening-cannot" role="alert" data-testid="app-cannot-open">
          {!withdrawn && (
            <p className="ap-opening-title">
              {title ? formatCopy(copy.appsCannotOpen, { app: title }) : copy.appsCannotOpenUnnamed}
            </p>
          )}
          <p className="ap-opening-body">
            {withdrawn
              ? formatCopy(pageCopy.noLongerShared, {
                  who: withdrawn.ownerName ?? sharingCopy.owner,
                })
              : (reason ?? copy.appsCannotOpenBody)}
          </p>
          {!withdrawn && (onRetry || change) && (
            <div className="ap-opening-actions">
              {onRetry && (
                <Button type="button" size="sm" variant="outline" onClick={onRetry}>
                  {retry}
                </Button>
              )}
              {change && (
                <Button type="button" size="sm" disabled={change.busy} onClick={change.open}>
                  <MessageSquareIcon aria-hidden="true" />
                  {changeLabel}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </AppStage>
  );
}

/** True for a saved canvas that is an App before its view says so: its card opened it, or it is the App's page. */
function useIsApp(target: ArtifactCanvasTarget | DraftCanvasTarget) {
  const ws = useOptionalWorkspace();
  const principal = useAppOpeningPrincipal();
  return (
    readHint(principal, target.id) !== undefined || Boolean(ws && isAppPageMode(ws.view, ws.canvas))
  );
}

/** A saved canvas still reading its view: an App's opening, or the plain loading state for any other Artifact. */
export function SavedCanvasLoading({
  target,
}: {
  target: ArtifactCanvasTarget | DraftCanvasTarget;
}) {
  const panels = useNamespaceCopy(artifactPanelsCopy);
  // The canvas's own loading words, never the workspace boot copy ("Loading your workspace…").
  const isApp = useIsApp(target);
  return isApp ? (
    <AppOpening artifactId={target.id} />
  ) : (
    <LoadingState label={panels.panelLoading} />
  );
}

/** A saved canvas whose view could not be read: an App's calm cannot-open, or the plain error for anything else. */
export function SavedCanvasError({
  target,
  message,
  onRetry,
}: {
  target: ArtifactCanvasTarget | DraftCanvasTarget;
  message: string;
  onRetry: () => void;
}) {
  return useIsApp(target) ? (
    <AppCannotOpen artifactId={target.id} onRetry={onRetry} />
  ) : (
    <ErrorState message={message} onRetry={onRetry} />
  );
}
